import { Grid } from '@mui/material';
import {
  useRecoilCallback,
  useRecoilState,
  useRecoilValue,
  useSetRecoilState,
} from 'recoil';
import {
  getAttributeMetaByIdCallback,
  postAttributeMetaCallback,
  putAttributeMetaCallback,
  removeNullishOrEmptyFields,
  AttributeMetaClass,
  attributeMetaLoaderState,
  attributeMetaState,
  getAttributesMetaByEntityTypeCallback,
  ATTRIBUTE_META_TYPES,
  getAllQuestionPageCallback,
  ENTITY_TYPES,
  AttributeMeta,
  snackBarState,
  SnackBarSeverity,
} from '@laborability/commons';
import ModalForm from '../ModalForm';
import { useEffect, useState } from 'react';
import {
  LBTAutocomplete,
  LBTButton,
  LBTSelect,
  LBTSwitch,
  LBTTextField,
} from '@laborability/components';
import { useNavigate } from 'react-router-dom';
import { APP_ROUTES } from '../../Routes';
import { LABEL_ENTITY_TYPE } from './PageAction';

export const LABEL_ATTRIBUTE_META_TYPE = {
  number: 'Numerico',
  string: 'Testuale',
  bool: 'Booleano',
  date: 'Data',
};

interface Props {
  id?: number;
  extraParams: { entity_type?: string; status_archived?: boolean };
}

export function AttributeMetaForm({ id, extraParams }: Props) {
  const navigate = useNavigate();
  const getAllPage = useRecoilCallback(getAllQuestionPageCallback, []);
  const postAttributeMeta = useRecoilCallback(postAttributeMetaCallback, []);
  const putAttributeMeta = useRecoilCallback(putAttributeMetaCallback, []);
  const getAttributeMeta = useRecoilCallback(getAttributeMetaByIdCallback, []);
  const getAllAttributesMeta = useRecoilCallback(
    getAttributesMetaByEntityTypeCallback,
    [],
  );
  const setSnackBar = useSetRecoilState(snackBarState);
  const [current, setCurrent] = useRecoilState(attributeMetaState);
  const [attributesMeta, setAttributesMeta] = useState<AttributeMeta[]>([]);
  const attributeMetaLoader = useRecoilValue(attributeMetaLoaderState);
  const resetFormState = () => {
    setCurrent(new AttributeMetaClass());
  };

  const getAttributesMeta = async () => {
    const res = await getAllAttributesMeta({
      entity_type: 'all',
      not_set: true,
    });
    if (res?.data) setAttributesMeta(res.data.items);
  };

  useEffect(() => {
    if (id) getAttributeMeta({ id });
    else resetFormState();
  }, [id]);

  useEffect(() => {
    getAttributesMeta();
  }, []);

  return (
    <ModalForm
      defaultValues={{
        ...new AttributeMetaClass(),
        entity_type: extraParams.entity_type,
      }}
      isLoading={attributeMetaLoader}
      currentValues={{ ...current, enableFormula: !!current.formula }}
      validation={values => {
        return {
          ...(values.attribute_name === ''
            ? { attribute_name: "Il nome dell'attributo è obbligatorio" }
            : {}),
          ...(values.attribute_type === ''
            ? { attribute_type: "Il tipo dell'attributo è obbligatorio" }
            : {}),
          /* ...(values.entity_type === ''
            ? { entity_type: "L'entità è obbligatoria" }
            : {}), */
        };
      }}
      handleSubmit={async values => {
        const {
          enableFormula,
          slug_entity_type,
          slug_attribute_meta_id,
          ...filteredValues
        } = values;

        if (filteredValues.id) {
          return await putAttributeMeta({
            id: filteredValues.id,
            ...removeNullishOrEmptyFields(filteredValues),
            formula: !!enableFormula ? filteredValues.formula : null,
          });
        }

        return await postAttributeMeta({
          ...removeNullishOrEmptyFields(filteredValues),
          formula: !!enableFormula ? filteredValues.formula : null,
        });
      }}
      resetGridState={async () => {
        if (extraParams.entity_type)
          await getAllAttributesMeta({
            entity_type: extraParams.entity_type,
            status_archived: extraParams.status_archived,
          });
      }}
      resetFormState={resetFormState}
    >
      {({ values, errors, handleBlur, setFieldValue }) => (
        <>
          <Grid item mobile={12}>
            <LBTTextField
              label="Nome"
              type="text"
              name="attribute_name"
              onChange={value => setFieldValue('attribute_name', value)}
              onBlur={handleBlur}
              value={values.attribute_name}
              required
              error={!!errors?.attribute_name}
              helperText={errors?.attribute_name}
            />
          </Grid>
          <Grid item mobile={12}>
            <LBTTextField
              label="Label"
              type="text"
              name="label"
              onChange={value => setFieldValue('label', value)}
              onBlur={handleBlur}
              value={values.label}
              required
              error={!!errors?.label}
              helperText={errors?.label}
            />
          </Grid>
          <Grid item mobile={12}>
            <LBTSelect
              id="attribute_type-select"
              name="attribute_type"
              value={values.attribute_type ?? undefined}
              label="Tipo"
              handleChange={e => setFieldValue('attribute_type', e)}
              onBlur={handleBlur}
              fullWidth
              required
              error={!!errors?.attribute_type}
              helperText={errors?.attribute_type}
              items={ATTRIBUTE_META_TYPES.map(item => ({
                id: item,
                name: LABEL_ATTRIBUTE_META_TYPE[item],
              }))}
            />
          </Grid>
          {/*  <Grid item mobile={12}>
            <LBTSelect
              id="entity_type-select"
              name="entity_type"
              value={values.entity_type ?? undefined}
              label="Entità"
              handleChange={e => setFieldValue('entity_type', e)}
              onBlur={handleBlur}
              disabled={!!values.id}
              fullWidth
              required
              error={!!errors?.entity_type}
              helperText={errors?.entity_type}
              items={ENTITY_TYPES.map(item => ({
                id: item,
                name: LABEL_ENTITY_TYPE[item],
              }))}
            />
          </Grid> */}
          {!!values.questions?.length ? (
            <Grid item mobile={6}>
              <LBTButton
                variant="outlined"
                onClick={async () => {
                  const question = values.questions?.filter(
                    item => !!item.flow_id && !!item.page_id,
                  );
                  const pageId = question?.[0].page_id;
                  const flowId = question?.[0].flow_id;
                  if (flowId) {
                    await getAllPage({ id: flowId });
                  }
                  navigate(`/${APP_ROUTES.QUESTION_PAGE}/${pageId}`);
                }}
              >
                Vai alla domanda collegata
              </LBTButton>
            </Grid>
          ) : (
            <Grid item mobile={4}>
              <LBTSwitch
                label={'Archiviato'}
                checked={values.archived}
                onChange={value => setFieldValue('archived', value)}
                direction="row"
                hasFullWidth
              />
            </Grid>
          )}
          <Grid item mobile={6}>
            <LBTSwitch
              label={'Abilita formula'}
              checked={values.enableFormula}
              onChange={value => setFieldValue('enableFormula', value)}
              direction="row"
              hasFullWidth
            />
          </Grid>
          {values.enableFormula && (
            <>
              <Grid item mobile={3}>
                <LBTSelect
                  id="slug_entity_type-select"
                  name="slug_entity_type"
                  value={values.slug_entity_type}
                  label="Tipo entità"
                  handleChange={e => setFieldValue('slug_entity_type', e)}
                  onBlur={handleBlur}
                  fullWidth
                  items={ENTITY_TYPES.map(item => ({
                    id: item,
                    name: LABEL_ENTITY_TYPE[item],
                  }))}
                  sx={{ minWidth: 0 }}
                  error={!!errors?.slug_entity_type}
                  helperText={errors?.slug_entity_type}
                />
              </Grid>
              <Grid item mobile={7}>
                <LBTAutocomplete
                  id="slug_attribute_meta_id-select"
                  name="slug_attribute_meta_id"
                  value={values.slug_attribute_meta_id}
                  label="Attributo"
                  handleChange={e => setFieldValue('slug_attribute_meta_id', e)}
                  onBlur={handleBlur}
                  fullWidth
                  disabled={!values.slug_entity_type}
                  items={attributesMeta
                    .filter(
                      item => item.entity_type === values.slug_entity_type,
                    )
                    .map(item => ({
                      id: item.id as number,
                      name: item.attribute_name as string,
                    }))}
                  getItems={async () => {}}
                  error={!!errors?.slug_attribute_meta_id}
                  helperText={errors?.slug_attribute_meta_id}
                />
              </Grid>
              <Grid item mobile={2}>
                <LBTButton
                  variant="contained"
                  fullWidth
                  onClick={() => {
                    const slug = attributesMeta.find(
                      item => item.id === values.slug_attribute_meta_id,
                    )?.slug;

                    if (slug) {
                      navigator.clipboard
                        .writeText(slug)
                        .then(() => {
                          setSnackBar({
                            show: true,
                            severity: SnackBarSeverity.success,
                            message: 'Slug copiato nella clipboard',
                            code: 200,
                            duration: 2000,
                          });
                        })
                        .catch(err => {
                          setSnackBar({
                            show: true,
                            severity: SnackBarSeverity.error,
                            message: `Errore durante la copia dello slug: ${err}`,
                            code: 400,
                            duration: 2000,
                          });
                        });
                    } else {
                      setSnackBar({
                        show: true,
                        severity: SnackBarSeverity.error,
                        message: 'Nessun attributo selezionato',
                        code: 404,
                        duration: 2000,
                      });
                    }
                  }}
                >
                  Copia Slug
                </LBTButton>
              </Grid>
              <Grid item mobile={12}>
                <LBTTextField
                  label="Formula"
                  type="text"
                  name="formula"
                  multiline
                  rows={4}
                  onChange={value => setFieldValue('formula', value)}
                  onBlur={handleBlur}
                  value={values.formula}
                  required
                  error={!!errors?.formula}
                  helperText={errors?.formula}
                />
              </Grid>
            </>
          )}
        </>
      )}
    </ModalForm>
  );
}
